import { type PopularProductsOffer } from '@koala/sdk';
import { getPopularProducts } from '@koala/sdk/v4';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from '@/redux';
import { createHttpClient } from '@/services/client';
import { __DANGEROUS_EMPTY_REQUEST_ORIGIN, getOrigin } from '@/utils';
import { toResolvedLocationId } from '@/utils/locations';
import { augmentMenuProduct } from '@/utils/menu';

/**
 * An api segment hook for fetching popular items for a specified location
 * ```
 * const { response, count, title, getProduct } = usePopularItems({ location: 1 });
 * ```
 */
export function usePopularItems({
  location,
  wantedAt,
  maxItems = 15,
}: {
  /**
   * Nullable because every caller derives it from redux, which holds `null` or
   * `undefined` until a location resolves. (TEAM-13663)
   */
  location: number | null | undefined;
  wantedAt?: string;
  maxItems?: number;
}) {
  const { popular_products: showPopularItems } = useSelector(
    (state) => state.app.cmsConfig.webConfig.menu,
  );

  const { popular_products_header: popularItemsHeader } = useSelector(
    (state) => state.app.cmsConfig.strings.store,
  );

  /**
   * Make sure that `usePopularItems` is prefetched before
   * this component renders, otherwise it will explode!
   * @see __DANGEROUS_EMPTY_REQUEST_ORIGIN
   */
  let origin: string;

  if (typeof window !== 'undefined') {
    origin = getOrigin(window.location.host);
  } else {
    origin = __DANGEROUS_EMPTY_REQUEST_ORIGIN;
  }

  const resolvedLocationId = toResolvedLocationId(location);

  const response = useQuery({
    queryKey: [
      'popular-items',
      {
        id: resolvedLocationId,
        wantedAt: wantedAt ?? 'asap',
      },
    ],
    queryFn: () => {
      /**
       * The previous `location ?? 0` fallback paired with `location !== null`
       * let an unresolved id through as `0` and requested popular products for
       * `store-locations/0` — the same defect TEAM-13663 fixed on the menu
       * route. `enabled` screens it now; the throw covers an imperative
       * refetch, which bypasses `enabled` in react-query v4.
       */
      if (resolvedLocationId === undefined) {
        throw new Error(
          `usePopularItems requires a resolved location id, received ${String(location)}`,
        );
      }

      return getPopularProducts(
        { locationId: resolvedLocationId, maxItems, wantedAt },
        { client: createHttpClient({ origin }) },
      );
    },
    enabled: showPopularItems && resolvedLocationId !== undefined,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    keepPreviousData: true,
  });

  return {
    response,
    count: response?.data?.products?.length ?? 0,
    title: popularItemsHeader,
    enabled: showPopularItems,
    getItem: function (product: PopularProductsOffer) {
      // popular products return a product array of 1 product
      return augmentMenuProduct(product.products[0]);
    },
  };
}
